import React from "react"
import * as CommonUtilities from "../../../common/utils/CommonUtils.js"

/* =============================================================================
   Product card — hybrid media.
   -----------------------------------------------------------------------------
   The image library is mostly 250x250. A card this size needs roughly 520px wide
   to look sharp on a modern screen, so upscaling most of the library would make
   the menu look worse, not better. Instead the card renders one of three ways and
   each is designed to look deliberate:

     rich     a photograph big enough to fill the card at 4:3
     compact  a small image shown at a size it can actually support, on a tint
     none     no usable image — the product name becomes the picture

   ⚠ The API returns no image dimensions, so quality cannot be known before the
   image loads. The card therefore starts in "compact" and promotes itself to
   "rich" in onLoad once the real width is known. Starting compact rather than
   rich matters: promoting looks like the image arriving, demoting would look
   like the layout breaking.

   ⚠ data-productid and data-productcategoryid MUST stay on the <button>.
   App.js reads them with event.currentTarget.getAttribute(...), so anything
   inside the button can change freely, but moving those attributes off it
   breaks every product click with no error.

   ⚠ REPLACES the old placeholder-image fallback. The previous card imported
   images/placeholder-image.png and showed it whenever productImageURL was empty.
   The "none" state above is the deliberate replacement: a product with no usable
   photo gets its name set as the picture instead of a grey placeholder graphic.
   The file is still in images/ if that decision is ever reversed — this is a
   design change, not an oversight.
   ============================================================================= */

// What the card needs to fill its 4:3 media area crisply on a 2x screen.
const RICH_MIN_WIDTH = 520

export default function Product(props) {
    const url = props.data.productImageURL
    const hasUrl = url !== null && url !== undefined && url !== ""

    // 'none' when there is nothing to load, otherwise start modest and promote.
    const [media, setMedia] = React.useState(hasUrl ? "compact" : "none")

    function handleImageLoad(event) {
        const w = event.target.naturalWidth
        if (w === 0) {
            // Loaded but zero pixels — a broken or placeholder response.
            setMedia("none")
        } else if (w >= RICH_MIN_WIDTH) {
            setMedia("rich")
        }
        // Anything in between stays compact, which is where it started.
    }

    function handleImageError() {
        setMedia("none")
    }

    // Same emptiness test the original card used — energyCount arrives as 0, "0",
    // "" or null depending on the product, and all four mean "do not show it".
    const ec = props.data.energyCount
    const energyCount = (ec !== 0 && ec !== "0" && ec !== "" && ec !== null && ec !== undefined)
        ? <span className="wo-kj">{ec}kJ</span>
        : null

    const price =
        <span className="wo-price">
            {CommonUtilities.StaticFields.CurrencySymbol}{props.data.priceInc.toFixed(2)}
        </span>

    const soldOut = props.data.isSoldOut
        ? <span className="wo-soldout"><span>SOLD OUT</span></span>
        : null

    // === begin: Bulk Pricing ===
    /* ⚠ Bulk-pricing only, and it must stay gated. The badge tells the customer a
       quantity break exists on this product; showing it on a brand without bulk
       pricing would promise a discount that cannot be earned. */
    let bulkPricingBadge = null
    if (CommonUtilities.StaticFields.PriceType === 'bp8826'
        && props.data.bulkPricingProductsPriceList
        && props.data.bulkPricingProductsPriceList.length > 0) {
        bulkPricingBadge = <span className="wo-kj">Buy more, save more</span>
    }
    // === end: Bulk Pricing ===

    // === begin: Product Description ===
    /* ⚠ Under bulk pricing the description carries the quantity breaks as a
       comma-separated list, rendered as separate lines in columns. That is why
       dangerouslySetInnerHTML is here — the commas become <br />. Kept exactly as
       the live card did it.

       The content is brand-authored product data from the API, not customer input,
       but this is still the one place in the card where markup is injected. Do not
       extend the same treatment to another field without thinking about it.

       `class=` in the original was a React mistake — silently ignored, so the
       column layout never actually applied. Corrected to className here. */
    let prodDescElement = props.data.productDescription
        ? <span className="wo-card__desc">{props.data.productDescription}</span>
        : null

    if (CommonUtilities.StaticFields.PriceType === 'bp8826'
        && props.data.productDescription
        && (props.data.productDescription).includes(',')) {
        const prodDescValue = (props.data.productDescription).split(',').join('<br />')
        prodDescElement =
            <span
                className="wo-card__desc columnsProdDesc"
                dangerouslySetInnerHTML={{ __html: prodDescValue }}
            ></span>
    }
    // === end: Product Description ===

    // === begin: Pickup Lead Time ===
    // Commented out 2026-10-01 — the customer did not want the feature. Kept so it
    // is not rediscovered as missing; CartPage carries the same note.
    // let pickupLeadTimeNote = null
    // if (CommonUtilities.StaticFields.PickupLeadTimeEnabled && props.data.webOrderingRequiresMinPickupDays && props.data.webOrderingMinPickupDaysMessage) {
    //     pickupLeadTimeNote = <span className="wo-card__lead">* {props.data.webOrderingMinPickupDaysMessage}</span>
    // }
    // === end: Pickup Lead Time ===

    // --- media area -----------------------------------------------------------
    let shot
    if (media === "none") {
        shot =
            <span className="wo-shot wo-shot--none">
                <span className="wo-shot__mono">{props.data.productName}</span>
                {energyCount}{price}{soldOut}
            </span>
    } else {
        shot =
            <span className={`wo-shot wo-shot--${media}`}>
                <img
                    src={url}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    className="wo-shot__img"
                />
                {energyCount}{price}{soldOut}
            </span>
    }

    return (
        <div className="wo-cardCell">
            <button
                type="button"
                className={`wo-card${props.data.isSoldOut ? " wo-card--out" : ""}`}
                data-productid={props.data.productId}
                data-productcategoryid={props.productCategoryId}
                onClick={props.data.isSoldOut ? null : props.handleProductMenuClick}
                disabled={props.data.isSoldOut ? true : false}
            >
                {shot}
                <span className="wo-card__body">
                    <span className="wo-card__name">{props.data.productName}</span>
                    {prodDescElement}
                    {/* Pickup lead time - commented out 2026-10-01: {pickupLeadTimeNote} */}
                    <span className="wo-card__foot">
                        {bulkPricingBadge}
                        <span className="wo-card__add">
                            {props.data.isSoldOut ? "Unavailable" : "Add"}
                        </span>
                    </span>
                </span>
            </button>
        </div>
    )
}
